import React from "react";

const RadarChart = ({ scoreAllList, createdAt, userName }) => {
  // Threshold legends mapping
  const legends = [
    { range: "0-6", text: "Least Used", dotColor: "bg-[#E53935]" },
    { range: "7-12", text: "Tertiary", dotColor: "bg-[#FFA000]" },
    { range: "13-18", text: "Secondary", dotColor: "bg-[#1E88E5]" },
    { range: "19-25", text: "Primary", dotColor: "bg-[#43A047]" },
  ];

  // Radar Chart Data
  const center = 200;
  const maxRadius = 150;
  const maxScore = 25;

  const deepThinker =
    scoreAllList.find((item) => item.name === "Deep Thinker")?.score || 0;

  const crowdPleaser =
    scoreAllList.find((item) => item.name === "Crowd Pleaser")?.score || 0;

  const flowGoer =
    scoreAllList.find((item) => item.name === "Flow Goer")?.score || 0;

  const hardCharger =
    scoreAllList.find((item) => item.name === "Hard Charger")?.score || 0;

  const getRadius = (score) => (score / maxScore) * maxRadius;

  const polygonPoints = [
    `${center},${center - getRadius(deepThinker)}`, // Top
    `${center + getRadius(crowdPleaser)},${center}`, // Right
    `${center},${center + getRadius(flowGoer)}`, // Bottom
    `${center - getRadius(hardCharger)},${center}`, // Left
  ].join(" ");

  const pointCoords = [
    {
      x: center,
      y: center - getRadius(deepThinker),
      color: "fill-sky-700",
    },
    {
      x: center + getRadius(crowdPleaser),
      y: center,
      color: "fill-purple-700",
    },
    {
      x: center,
      y: center + getRadius(flowGoer),
      color: "fill-teal-600",
    },
    {
      x: center - getRadius(hardCharger),
      y: center,
      color: "fill-rose-700",
    },
  ];

  return (
    <div className="p-10 px-6 space-y-6 bg-white">
      <div className="px-12 space-y-6 page-break">
        {/* Header */}
        <div className="flex items-center justify-between w-full pt-4 pb-2 border-b text-[#212121]/85 italic">
          <h1 className="text-lg font-semibold">Flow Profile Report</h1>
          <p className="text-lg font-semibold">{userName} | {createdAt}</p>
        </div>

        {/* Title */}
        <div className="mt-6 mb-4">
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">
            Flow Profile Map (Score 0-25 per profile)
          </h1>
          <p className="text-base text-gray-600 mt-1">
            A snapshot of your scores across the four flow profiles.
          </p>
        </div>

        {/* Radar Chart */}
        <div className="relative w-full max-w-lg mx-auto aspect-square my-8 flex items-center justify-center">

          {/* Top */}
          <div className="absolute top-0 text-center">
            <span className="block font-bold text-sky-700 text-lg">
              Deep Thinker
            </span>
            <span className="block font-bold text-sky-700 text-lg">
              {"( " + deepThinker + " )"}
            </span>
          </div>

          {/* Right */}
          <div className="absolute -right-24 text-right">
            <span className="block font-bold text-purple-700 text-lg">
              Crowd Pleaser
            </span>
            <span className="block font-bold text-purple-700 text-lg">
              {"( " + crowdPleaser + " )"}
            </span>
          </div>

          {/* Bottom */}
          <div className="absolute bottom-0 text-center">
            <span className="block font-bold text-teal-700 text-lg">
              Flow Goer
            </span>
            <span className="block font-bold text-teal-700 text-lg">
              {"( " + flowGoer + " )"}
            </span>
          </div>

          {/* Left */}
          <div className="absolute -left-24 text-left">
            <span className="block font-bold text-rose-700 text-lg">
              Hard Charger
            </span>
            <span className="block font-bold text-rose-700 text-lg">
              {"( " + hardCharger + " )"}
            </span>
          </div>

          <svg
            viewBox="0 0 400 400"
            className="w-[90%] h-[90%] overflow-visible"
          >
            {/* Grid Rings */}
            {[5, 10, 15, 20, 25].map((val) => {
              const radius = (val / maxScore) * maxRadius;

              return (
                <g key={val}>
                  <polygon
                    points={`
                      ${center},${center - radius}
                      ${center + radius},${center}
                      ${center},${center + radius}
                      ${center - radius},${center}
                    `}
                    fill="none"
                    stroke="#e2e8f0"
                    strokeWidth="1"
                  />

                  <text
                    x={center}
                    y={center - radius + 4}
                    className="text-[14px] fill-gray-700 font-medium"
                    textAnchor="middle"
                  >
                    {val}
                  </text>
                </g>
              );
            })}

            {/* Center Label */}
            <text
              x={center}
              y={center + 4}
              className="text-[12px] fill-gray-700 font-medium"
              textAnchor="middle"
            >
              0
            </text>

            {/* Axis Lines */}
            <line
              x1={center}
              y1={center - maxRadius}
              x2={center}
              y2={center + maxRadius}
              stroke="#e2e8f0"
              strokeWidth="1"
            />

            <line
              x1={center - maxRadius}
              y1={center}
              x2={center + maxRadius}
              y2={center}
              stroke="#e2e8f0"
              strokeWidth="1"
            />

            {/* Radar Area */}
            <polygon
              points={polygonPoints}
              fill="rgba(20,184,166,0.08)"
              stroke="#14b8a6"
              strokeWidth="3"
            />

            {/* Data Points */}
            {pointCoords.map((point, index) => (
              <circle
                key={index}
                cx={point.x}
                cy={point.y}
                r="2"
                className={point.color}
              />
            ))}
          </svg>
        </div>

        {/* Legends */}
        <div className="border border-gray-300 rounded-xl bg-gray-50/50 px-12 py-4 grid grid-cols-2 sm:flex sm:flex-wrap items-center justify-between gap-4 mb-6">
          {legends.map((item, i) => (
            <div
              key={i}
              className="flex items-center gap-2 text-base mx-auto sm:mx-0 text-gray-900"
            >
              <span
                className={`w-2.5 h-2.5 rounded-full ${item.dotColor}`}
              />
              <span className="block font-medium">{item.range}</span>
              <span className="font-medium">{item.text}</span>
            </div>
          ))}
        </div>

        {/* Score Cards */}
        <div className="border border-gray-300 rounded-xl p-4 grid grid-cols-2 md:grid-cols-4 gap-y-6 divide-x-0 sm:divide-x divide-gray-300">
          {scoreAllList.map((card, idx) => (
            <div
              key={idx}
              className="flex flex-col items-center justify-center text-center px-2 space-y-2"
            >
              <span className={`text-lg font-bold ${card.scoreColor}`}>
                {card.name}
              </span>

              <div className="text-lg font-bold text-gray-800">
                <span className={card.scoreColor}>{card.score + " / "}</span>
                <span className="text-gray-400 font-bold text-sm">
                  {" " + card.maxScore}
                </span>
              </div>

              <span className={`text-base font-bold px-2.5 py-1 w-32 text-center rounded border tracking-wider ${card.colorClass}`} >
                {card.status}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default RadarChart;